/**
 * ETERNITY INTELLIGENCE — DESKTOP SLIM NAVIGATION
 * Icon rail for desktop
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, Users, Sparkles, FileText, User, Settings
} from 'lucide-react';

const DesktopNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  const navItems = [
    { id: 'home', label: 'Home', icon: Home, path: '/intelligence', color: '#E85002' },
    { id: 'clients', label: 'Clients', icon: Users, path: '/intelligence/clients', color: '#F16001' },
    { id: 'ai', label: 'AI', icon: Sparkles, path: '/intelligence/ask', color: '#FF6129', special: true },
    { id: 'website', label: 'Website', icon: FileText, path: '/intelligence/website', color: '#FDA136' },
    { id: 'me', label: 'Me', icon: User, path: '/intelligence/me', color: '#C10801' }
  ];

  const isActive = (path) => {
    if (path === '/intelligence') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="hidden md:flex fixed left-0 top-0 bottom-0 z-40 flex-col items-center py-6 transition-all duration-300"
      style={{
        width: expanded ? '180px' : '80px',
        background: 'rgba(17, 17, 17, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(255, 255, 255, 0.05)'
      }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      {/* Logo / Brand */}
      <div className="mb-10 px-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-light"
          style={{
            background: 'linear-gradient(135deg, #E85002 0%, #C10801 100%)',
            color: '#F9F9F9'
          }}
        >
          E
        </div>
      </div>

      {/* Main Nav */}
      <div className="flex-1 w-full space-y-2 px-3">
        {navItems.map(item => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden"
              style={{
                background: active
                  ? `linear-gradient(135deg, ${item.color}20 0%, ${item.color}10 100%)`
                  : 'transparent',
                color: active ? item.color : '#A7A7A7'
              }}
            >
              {/* Hover glow */}
              {!active && (
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: `radial-gradient(circle at center, ${item.color}15 0%, transparent 70%)`
                  }}
                />
              )}

              <Icon 
                size={item.special ? 24 : 22} 
                strokeWidth={active ? 2 : 1.5} 
                className="relative z-10 flex-shrink-0" 
              />
              
              {expanded && (
                <span
                  className="text-sm font-light tracking-wide relative z-10 whitespace-nowrap"
                  style={{
                    color: active ? item.color : '#F9F9F9'
                  }}
                >
                  {item.label}
                </span>
              )}

              {active && (
                <div
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 w-1 h-8 rounded-l-full"
                  style={{ background: item.color }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Settings at bottom */}
      <div className="w-full px-3 pt-4 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
        <button
          onClick={() => navigate('/intelligence/settings')}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300"
          style={{
            color: '#646464'
          }}
        >
          <Settings size={20} strokeWidth={1.5} />
          {expanded && (
            <span className="text-sm font-light tracking-wide">
              Settings
            </span>
          )}
        </button>
      </div>
    </nav>
  );
};

export default DesktopNav;
